import React, { useState } from 'react';
import { Typography, Box } from '@mui/material';
import TimeField from 'packages/novous_web/src/common/components/TimeField';
import InputField from 'packages/novous_web/src/common/components/InputField';
import AlertToast from 'packages/novous_web/src/common/components/Alert';

interface FeatureFields {
  [fieldName: string]: [string, any];
}

interface Feature {
  name: string;
  fields: FeatureFields;
  enableFlag: boolean;
  authenticatedUSerEnabled: boolean; // Fixed typo in property name
}

interface FeatureSectionProps {
  feature: Feature;
  name: string;
  isSuperAdmin: boolean;
  onToggleFeatureFlag: (featureKey: string, flagType: 'enableFlag' | 'authenticatedUSerEnabled') => void;
  onUpdateFieldValue: (featureKey: string, fieldName: string, newValue: any) => void;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
  feature,
  name,
  isSuperAdmin,
  onToggleFeatureFlag,
  onUpdateFieldValue,
}) => {
  if (!feature) return null;

  const formatFeatureName = (name: string) => {
    return name
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatLabel = (label: string) => {
    if (!label) return '';
    const snakeToSpace = label.replace(/_/g, ' ');
    const formatted = snakeToSpace.replace(/([a-z])([A-Z])/g, '$1 $2');
    return formatted
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const isEnabled = feature.enableFlag;
  const shouldForceEnable = isSuperAdmin && feature.authenticatedUSerEnabled;
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

  const parseTime = (time: string): number => {
    if (!time) return 0;
    if (/AM|PM/.test(time)) {
      const [timePart, meridiem] = time.split(' ');
      let [hour, minute] = timePart.split(':').map(Number);

      if (meridiem === 'PM' && hour !== 12) hour += 12;
      if (meridiem === 'AM' && hour === 12) hour = 0;

      return hour * 60 + minute;
    } else {
      const [hour, minute] = time.split(':').map(Number);
      return hour * 60 + minute;
    }
  };

  const handleFieldUpdate = (fieldName: string, newValue: any) => {
    // Special validation for nightDriving times
    if (feature.name === 'nightDriving' && (fieldName === 'startTime' || fieldName === 'endTime')) {
      const otherFieldName = fieldName === 'startTime' ? 'endTime' : 'startTime';
      const otherTimeValue = feature.fields[otherFieldName]?.[1];

      if (otherTimeValue) {
        const currentTime = parseTime(newValue);
        const otherTime = parseTime(otherTimeValue);

        const isInvalid =
          (fieldName === 'startTime' && currentTime >= otherTime) ||
          (fieldName === 'endTime' && currentTime <= otherTime);

        if (isInvalid) {
          setToast({
            open: true,
            message: 'Start time must be less than end time',
            type: 'error',
          });
          return;
        }
      }
    }

    onUpdateFieldValue(feature.name, fieldName, newValue);
  };

  return (
    <div
      className="flex flex-col gap-4 border border-[#F0EFF8] p-4 rounded-lg"
      style={{
        opacity: isSuperAdmin ? 1 : feature.authenticatedUSerEnabled ? 1 : 0.5,
      }}
    >
      <div className="flex justify-between items-center">
        <Typography variant="h6" fontWeight={600} fontSize={18}>
          {formatFeatureName(name)}
        </Typography>

        <label className="relative inline-flex items-center cursor-pointer">
          <Box
            className="w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out"
            sx={(theme) => ({
              backgroundColor:
                shouldForceEnable || isEnabled ? theme.palette.primary.main : theme.palette.secondary.main,
            })}
          >
            <div
              className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${
                shouldForceEnable || isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </Box>
          <input
            type="checkbox"
            className="sr-only peer"
            checked={shouldForceEnable || isEnabled}
            onChange={() => {
              if (isSuperAdmin) {
                // For admin, toggle the flag they clicked on
                const flagType = shouldForceEnable ? 'authenticatedUSerEnabled' : 'enableFlag';
                onToggleFeatureFlag(name, flagType);
              } else if (feature.authenticatedUSerEnabled) {
                // For non-admin, only toggle enableFlag if authenticatedUSerEnabled is true
                onToggleFeatureFlag(name, 'enableFlag');
              }
            }}
            disabled={!isSuperAdmin && !feature.authenticatedUSerEnabled}
          />
        </label>
      </div>

      {Object.keys(feature.fields).map((fieldName: string, idx: number) => {
        if (fieldName === 'enableFlag') return null;
        const [type, value] = feature.fields[fieldName];
        const isEditable = isSuperAdmin || feature.authenticatedUSerEnabled;

        return (
          <div key={idx} className="flex gap-1 mt-2">
            {feature.name === 'nightDriving' && (fieldName === 'startTime' || fieldName === 'endTime') ? (
              <TimeField
                label={formatLabel(fieldName)}
                value={value}
                onChange={(val: any) => handleFieldUpdate(fieldName, val)}
                showSeconds={false}
                name=""
                isDisabled={!isEditable}
              />
            ) : (
              <InputField
                label={formatLabel(fieldName)}
                type={type === 'Integer' || type === 'long' ? 'number' : 'text'}
                value={typeof value === 'boolean' ? String(value) : value}
                onChange={(e: any) => onUpdateFieldValue(feature.name, fieldName, e.target.value)}
                isDisabled={!isEditable || !feature.enableFlag}
              />
            )}
          </div>
        );
      })}
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </div>
  );
};

export default FeatureSection;
